import type {
  IAssistantRow,
  IAvailabilityValue,
} from '@/interfaces/assistant.interface';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import { buildGpthelpVisibilityCtx } from '../gpthelp-host-ctx-builders';
import { resolveOfferedMenus } from '../menu/gpthelp-admin-catalog';
import {
  resolveCategoryVisibility,
  resolveItemVisibility,
} from '../menu/gpthelp-admin-menu-visibility';
import type { IGpthelpResolvedHiddenReason } from '../menu/gpthelp-admin-menu-visibility';
import type { ICartadigitalAdminHostToolDeps } from '../tools/tool-types';
import { rowEntity } from './row-presenters';
import { isAvailabilityValue, isFlagValue } from './row-values';

/** What else hides something a row turns on (plan P0-3), in the order the card names them. */
export const STILL_HIDDEN_REASONS = [
  'categoryDisabled',
  'categoryHiddenInBranch',
  'categoryPosOnly',
  'outsideHours',
  'menuNotOffered',
] as const;
export type TStillHiddenReason = typeof STILL_HIDDEN_REASONS[number];

/**
 * The reasons of the read tools' visibility that the row's subject does not
 * decide itself; the product's own switches (`item_disabled`,
 * `item_hidden_in_branch`) are what the row changes, and a product without
 * its category is not on the menu at all.
 */
const STILL_HIDDEN_OF: Partial<
  Record<IGpthelpResolvedHiddenReason, TStillHiddenReason>
> = {
  category_disabled: 'categoryDisabled',
  category_hidden_in_branch: 'categoryHiddenInBranch',
  category_pos_only: 'categoryPosOnly',
  item_outside_hours: 'outsideHours',
  category_outside_hours: 'outsideHours',
  menu_not_offered: 'menuNotOffered',
};

/**
 * What else hides a category a row turns on (plan P2-4): it is sold only at
 * the Point of Sale, it is outside its hours, or its menu is not offered.
 * Its own switches (`category_disabled`, `category_hidden_in_branch`) are
 * what the row changes.
 */
const CATEGORY_STILL_HIDDEN_OF: Partial<
  Record<IGpthelpResolvedHiddenReason, TStillHiddenReason>
> = {
  category_pos_only: 'categoryPosOnly',
  category_outside_hours: 'outsideHours',
  menu_not_offered: 'menuNotOffered',
};

type TStillHiddenDeps = Pick<
  ICartadigitalAdminHostToolDeps,
  'menuStore' | 'restaurantStore'
>;

/** The on/off switches of a product or a category as an availability value leaves them. */
function switchesOf(
  value: IAvailabilityValue
): Pick<ICategory, 'visible' | 'hiddenBranchesIds'> {
  return {
    visible: value.visible,
    hiddenBranchesIds:
      value.hiddenBranchIds === null ? null : value.hiddenBranchIds.map(String),
  };
}

/**
 * A category as the proposal's rows leave it: its own availability and
 * where it is sold (plan P2-4: the rows of one view read their category's
 * `to`).
 */
function categoryLeftBy(
  category: ICategory,
  rows: readonly IAssistantRow[]
): ICategory {
  let left = category;
  for (const row of rows) {
    if (rowEntity(row) !== 'category' || row.categoryId !== category.id)
      continue;
    if (row.field === 'availability' && isAvailabilityValue(row.to))
      left = { ...left, ...switchesOf(row.to) };
    else if (row.field === 'posOnly' && isFlagValue(row.to))
      left = { ...left, posOnly: row.to.on };
  }
  return left;
}

/** Customers here are offered the category's menu (the base menu stands in while menus load). */
function menuOfferedFor(category: ICategory, deps: TStillHiddenDeps): boolean {
  return resolveOfferedMenus(deps.menuStore.availableMenus).some(
    (menu) => (menu.id ?? null) === (category.menuId ?? null)
  );
}

/** The reasons `map` words, deduplicated, in the order the card names them. */
function stillHiddenOf(
  hiddenReasons: readonly IGpthelpResolvedHiddenReason[],
  map: Partial<Record<IGpthelpResolvedHiddenReason, TStillHiddenReason>>
): TStillHiddenReason[] {
  const found = new Set(hiddenReasons.map((reason) => map[reason]));
  return STILL_HIDDEN_REASONS.filter((reason) => found.has(reason));
}

/** What still hides a product the row turns on: its category (as the view leaves it), its menu or its hours. */
function productStillHidden(
  row: IAssistantRow,
  to: IAvailabilityValue,
  deps: TStillHiddenDeps,
  rows: readonly IAssistantRow[]
): TStillHiddenReason[] {
  const live =
    row.itemId === null ? null : deps.menuStore.itemMap?.[row.itemId];
  if (!live) return [];
  // The product as the row leaves it.
  const item: IItem = { ...live, ...switchesOf(to) };
  const liveCategory = deps.menuStore.categoryMap?.[item.categoryId] ?? null;
  const category =
    liveCategory === null ? null : categoryLeftBy(liveCategory, rows);
  const { hiddenReasons } = resolveItemVisibility({
    item,
    category,
    menuOffered: category !== null && menuOfferedFor(category, deps),
    ctx: buildGpthelpVisibilityCtx({ restaurantStore: deps.restaurantStore }),
  });
  return stillHiddenOf(hiddenReasons, STILL_HIDDEN_OF);
}

/** What still hides a category the row turns on: where it is sold, its hours or its menu. */
function categoryStillHidden(
  row: IAssistantRow,
  to: IAvailabilityValue,
  deps: TStillHiddenDeps,
  rows: readonly IAssistantRow[]
): TStillHiddenReason[] {
  const live =
    row.categoryId === null
      ? null
      : deps.menuStore.categoryMap?.[row.categoryId];
  if (!live) return [];
  // The category as the rest of the view leaves it, then as this row does.
  const category: ICategory = {
    ...categoryLeftBy(live, rows),
    ...switchesOf(to),
  };
  const { hiddenReasons } = resolveCategoryVisibility({
    category,
    menuOffered: menuOfferedFor(category, deps),
    ctx: buildGpthelpVisibilityCtx({ restaurantStore: deps.restaurantStore }),
  });
  return stillHiddenOf(hiddenReasons, CATEGORY_STILL_HIDDEN_OF);
}

/**
 * Why something a row turns on (a product or, from Phase 2, a category) is
 * still hidden from customers here right now: its category, where it is
 * sold, its menu or its hours, with the same visibility rules as the read
 * tools, the categories as `rows` (the proposal's rows) leave them; [] when
 * nothing else hides it, or when the row turns nothing on.
 */
export function stillHiddenReasons(
  row: IAssistantRow,
  deps: TStillHiddenDeps,
  rows: readonly IAssistantRow[] = []
): TStillHiddenReason[] {
  if (row.field !== 'availability') return [];
  if (!isAvailabilityValue(row.to) || !row.to.available) return [];
  switch (rowEntity(row)) {
    case 'item':
      return productStillHidden(row, row.to, deps, rows);
    case 'category':
      return categoryStillHidden(row, row.to, deps, rows);
    default:
      return [];
  }
}
